import React from "react";
import { Box, Link, Stack, Typography } from "@mui/material";
import { LegalPage } from "../components/LegalPage.tsx";

const Section = ({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ mt: 4 }}>
    <Typography variant="h6" component="h3" gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
);

const BulletList = ({ items }: { items: React.ReactNode[] }) => (
  <Typography component="ul" sx={{ pl: 3, mt: 1, mb: 2 }}>
    {items.map((item, index) => (
      <li key={index}>
        <Typography component="span">{item}</Typography>
      </li>
    ))}
  </Typography>
);

export const PrivacyPage: React.FC = () => {
  const privacyContent = (
    <>
      <Typography variant="h5" component="h2" gutterBottom>
        Privacyverklaring Pippaya Hondenopvang
      </Typography>

      <Typography paragraph>
        Pippaya Hondenopvang gaat zorgvuldig om met persoonsgegevens. In deze
        privacyverklaring leggen we uit welke gegevens we verwerken, waarom we
        dat doen, met wie gegevens kunnen worden gedeeld, hoe lang we gegevens
        bewaren en welke rechten je hebt.
      </Typography>

      <Section title="1. Wie is verantwoordelijk voor je gegevens?">
        <Typography paragraph>
          Pippaya Hondenopvang is verantwoordelijk voor de verwerking van
          persoonsgegevens binnen de opvang, de website en Mijn Pippaya.
        </Typography>
        <Typography paragraph>
          Pippaya Hondenopvang
          <br />
          Boezem 7, Giessenburg
          <br />
          KvK 96754974
          <br />
          E-mail:{" "}
          <Link href="mailto:pippaya.nl@gmail.com">
            pippaya.nl@gmail.com
          </Link>
        </Typography>
      </Section>

      <Section title="2. Welke gegevens verwerken we?">
        <Typography paragraph>
          Afhankelijk van je contact met Pippaya kunnen we onder andere de
          volgende gegevens verwerken:
        </Typography>
        <BulletList
          items={[
            "naam, e-mailadres en telefoonnummer;",
            "gegevens die nodig zijn voor een Mijn Pippaya-account en de koppeling met het klantprofiel;",
            "gegevens over je hond, zoals naam, ras of type, geslacht, geboortedatum, castratie- of sterilisatiestatus, loopsheidsinformatie en relevante verzorgings- of gedragsinformatie;",
            "opvangaanvragen, gewenste opvangdata, opties, reserveringen, aankomst- en vertrektijden en annuleringsinformatie;",
            "contactgegevens van een opgegeven achtervang wanneer die voor de opvang worden verstrekt;",
            "offertes, facturen, betalingen en andere gegevens die onderdeel zijn van de financiële administratie;",
            "berichten en overige informatie die je zelf aan Pippaya doorgeeft;",
            "foto's en filmpjes die tijdens de opvang worden gemaakt;",
            "technische gegevens die nodig zijn voor beveiliging, accounttoegang en het goed functioneren van de website."
          ]}
        />
        <Typography paragraph>
          Gegevens over de hond zijn op zichzelf meestal geen persoonsgegevens,
          maar kunnen onderdeel zijn van je klantdossier wanneer ze aan jou als
          eigenaar zijn gekoppeld. Daarom behandelen we die gegevens binnen
          Pippaya zorgvuldig.
        </Typography>
      </Section>

      <Section title="3. Waarvoor gebruiken we deze gegevens en op welke grondslag?">
        <BulletList
          items={[
            "Voor het behandelen van een opvangaanvraag, het beoordelen van beschikbaarheid en geschiktheid en het voorbereiden van een mogelijke opvangovereenkomst. Dit gebeurt omdat je zelf om opvang vraagt en dus op jouw verzoek stappen worden gezet vóór een eventuele overeenkomst.",
            "Voor het uitvoeren van een reservering en de opvang, waaronder communicatie, verzorging, planning, annulering en eventuele noodsituaties. Dit is nodig voor de uitvoering van de overeenkomst.",
            "Voor offertes, facturen, betalingen en de administratie. Voor een deel hiervan geldt een wettelijke administratieve en fiscale bewaarplicht.",
            "Voor beveiliging van Mijn Pippaya, toegangscontrole, het voorkomen en onderzoeken van misbruik en het betrouwbaar laten functioneren van de website. Hierbij heeft Pippaya een gerechtvaardigd belang bij een veilige bedrijfsvoering.",
            "Voor capaciteits- en vraaganalyse. Bij een beschikbaarheidscontrole bewaren we onder andere het soort opvang, de gewenste periode, het geslacht van de hond, de uitkomst van de controle en een willekeurige formuliercode. In deze statistiek worden naam, e-mailadres en telefoonnummer niet rechtstreeks opgeslagen. Wanneer daarna daadwerkelijk een aanvraag wordt ingediend, kan de statistiekregel technisch aan die aanvraag worden gekoppeld. Dit gebruiken we om drukke en rustige opvangperiodes te herkennen en de planning te verbeteren.",
            "Voor herkenbare foto's of filmpjes van personen gebruiken we toestemming. Voor foto's en filmpjes van honden respecteren we de afspraken en bezwaren die de eigenaar aan Pippaya doorgeeft."
          ]}
        />
      </Section>

      <Section title="4. Met wie kunnen gegevens worden gedeeld?">
        <Typography paragraph>
          Pippaya verkoopt persoonsgegevens niet. Voor de bedrijfsvoering maken
          we wel gebruik van dienstverleners die gegevens voor of samen met
          Pippaya kunnen verwerken. Op dit moment gaat het onder andere om:
        </Typography>
        <BulletList
          items={[
            "Supabase voor de database, accountkoppeling en inlogfunctionaliteit van Mijn Pippaya;",
            "Moneybird voor offertes, facturen, betalingen en de financiële administratie;",
            "EmailJS voor het versturen van de e-mailmelding die hoort bij een opvangaanvraag;",
            "een dierenarts of dierenkliniek wanneer dit nodig is voor de verzorging of gezondheid van een hond;",
            "overheidsinstanties of andere partijen wanneer Pippaya wettelijk verplicht is gegevens te verstrekken."
          ]}
        />
        <Typography paragraph>
          Waar een partij persoonsgegevens uitsluitend in opdracht van Pippaya
          verwerkt, worden daarvoor de vereiste afspraken over
          gegevensverwerking gebruikt.
        </Typography>
      </Section>

      <Section title="5. Verwerking buiten de Europese Economische Ruimte">
        <Typography paragraph>
          Sommige technische dienstverleners kunnen gebruikmaken van
          infrastructuur of subverwerkers buiten de Europese Economische Ruimte
          (EER). Wanneer persoonsgegevens buiten de EER worden verwerkt, gebeurt
          dit alleen wanneer daarvoor een geldige wettelijke doorgiftegrond en
          passende waarborgen bestaan, bijvoorbeeld een
          adequaatheidsbesluit of de door de Europese Commissie goedgekeurde
          standaardcontractbepalingen.
        </Typography>
      </Section>

      <Section title="6. Hoe lang bewaren we gegevens?">
        <Typography paragraph>
          We bewaren persoonsgegevens niet langer dan nodig is voor het doel
          waarvoor ze zijn verzameld, tenzij een wettelijke bewaarplicht of een
          gerechtvaardigde reden vraagt om een langere termijn. Als uitgangspunt
          gebruikt Pippaya de volgende termijnen:
        </Typography>
        <BulletList
          items={[
            "Een opvangaanvraag die niet tot een klantrelatie of reservering leidt: in beginsel maximaal 12 maanden nadat de aanvraag is afgehandeld, tenzij de gegevens langer nodig zijn voor een geschil of andere aantoonbare reden.",
            "Klant-, honden- en reserveringsgegevens: zolang de klantrelatie actief is. Wanneer er gedurende 2 jaar geen contact of opvang meer is geweest, beoordelen we welke gegevens nog nodig zijn en verwijderen we gegevens die niet langer nodig zijn.",
            "Gegevens die onderdeel zijn van de fiscale administratie, zoals facturen en betalingsgegevens: minimaal gedurende de wettelijke bewaartermijn, in de regel 7 jaar.",
            "Registraties van beschikbaarheids- en opvangvraag: in beginsel maximaal 24 maanden op detailniveau. Daarna worden ze verwijderd of, waar mogelijk, losgekoppeld van een individuele aanvraag zodat alleen bruikbare statistische informatie overblijft.",
            "Gegevens die aantoonbaar nodig zijn voor een lopend geschil, openstaande betaling of wettelijke verplichting kunnen langer worden bewaard zolang dat noodzakelijk is."
          ]}
        />
      </Section>

      <Section title="7. Mijn Pippaya en beveiliging">
        <Typography paragraph>
          Pippaya neemt passende technische en organisatorische maatregelen om
          persoonsgegevens te beschermen tegen verlies, onbevoegde toegang en
          ongewenste wijziging. Toegang tot beheerfuncties is beperkt en klanten
          kunnen via Mijn Pippaya alleen de gegevens bekijken en wijzigen die
          voor hun eigen account beschikbaar zijn.
        </Typography>
        <Typography paragraph>
          Houd je eigen wachtwoord geheim en neem contact op met Pippaya wanneer
          je vermoedt dat iemand anders toegang tot je account heeft gekregen.
        </Typography>
      </Section>

      <Section title="8. Cookies en technische opslag">
        <Typography paragraph>
          Pippaya gebruikt op dit moment geen advertentie- of
          marketingcookies. Voor Mijn Pippaya kan technisch noodzakelijke
          browseropslag worden gebruikt om inloggen, beveiliging en sessies te
          laten functioneren. Voor zover deze opslag strikt noodzakelijk is
          voor de gevraagde dienst, wordt daarvoor geen marketingtoestemming
          gevraagd.
        </Typography>
      </Section>

      <Section title="9. Geautomatiseerde controles">
        <Typography paragraph>
          Pippaya gebruikt geen geautomatiseerde besluitvorming over personen
          zoals bedoeld in artikel 22 AVG. De website kan wel automatisch
          controleren of een opvangperiode volgens de actuele planning
          beschikbaar is en of een ras of rastype volgens het opvangbeleid niet
          voor een nieuwe aanvraag wordt aangenomen. Deze controles ondersteunen
          de praktische intake en planning; Pippaya beoordeelt zelf of een hond
          uiteindelijk binnen de opvang en de groep past.
        </Typography>
      </Section>

      <Section title="10. Je privacyrechten">
        <Typography paragraph>
          Je kunt Pippaya verzoeken om inzage in je persoonsgegevens, correctie,
          verwijdering, beperking van de verwerking of overdracht van gegevens.
          Je kunt in bepaalde gevallen ook bezwaar maken tegen een verwerking.
          Wanneer een verwerking op toestemming is gebaseerd, kun je die
          toestemming voor de toekomst intrekken.
        </Typography>
        <Typography paragraph>
          Stuur een verzoek naar{" "}
          <Link href="mailto:pippaya.nl@gmail.com">
            pippaya.nl@gmail.com
          </Link>
          . Pippaya kan om aanvullende informatie vragen wanneer dat nodig is om
          te controleren dat het verzoek werkelijk van jou afkomstig is.
        </Typography>
      </Section>

      <Section title="11. Klacht indienen">
        <Typography paragraph>
          Heb je een klacht over de manier waarop Pippaya met persoonsgegevens
          omgaat, neem dan eerst contact op zodat we dit kunnen proberen op te
          lossen. Je hebt daarnaast het recht om een klacht in te dienen bij de
          Autoriteit Persoonsgegevens.
        </Typography>
      </Section>

      <Section title="12. Verplichte en optionele gegevens">
        <Typography paragraph>
          Zonder de gegevens die in een opvangaanvraag als verplicht zijn
          aangegeven, kan Pippaya de aanvraag niet goed beoordelen of contact
          met je opnemen. Vrije toelichtingen en andere niet-verplichte velden
          kun je leeg laten wanneer ze niet relevant zijn.
        </Typography>
      </Section>

      <Section title="13. Wijzigingen in deze privacyverklaring">
        <Typography paragraph>
          Wanneer de werkwijze, website of gebruikte dienstverleners
          veranderen, kan Pippaya deze privacyverklaring aanpassen. Op deze
          pagina staat steeds de actuele versie en de datum van de laatste
          wijziging.
        </Typography>
      </Section>
    </>
  );

  return (
    <LegalPage
      title="Privacyverklaring"
      lastUpdated="2 oktober 2026"
      content={privacyContent}
    />
  );
};
